import type { AnnotationEditorState, AnnotationShape, AnnotationTool } from './types';

export type AnnotationAction =
  | { type: 'SET_TOOL'; tool: AnnotationTool }
  | { type: 'SET_COLOR'; color: string }
  | { type: 'START_DRAWING'; shape: AnnotationShape }
  | { type: 'UPDATE_DRAWING'; shape: AnnotationShape }
  | { type: 'COMMIT_DRAWING' }
  | { type: 'CANCEL_DRAWING' }
  | { type: 'INSERT_SHAPE'; shape: AnnotationShape }
  | { type: 'CYCLE_SELECTION'; direction: 'next' | 'prev' }
  | { type: 'SELECT_SHAPE'; id: string; additive: boolean }
  | { type: 'DESELECT_ALL' }
  | { type: 'DELETE_SELECTED' }
  | { type: 'MOVE_SHAPES'; ids: string[]; dx: number; dy: number }
  | { type: 'RESIZE_SHAPE'; id: string; shape: AnnotationShape }
  | { type: 'RESIZE_SELECTED'; dw: number; dh: number }
  | { type: 'ROTATE_SHAPE'; id: string; rotation: number }
  | { type: 'ROTATE_SELECTED'; delta: number }
  | { type: 'CHANGE_SELECTED_COLOR'; color: string }
  | { type: 'ERASE_AT'; shapes: AnnotationShape[] }
  | { type: 'LOAD_SHAPES'; shapes: AnnotationShape[] }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'DISCARD' }
  | { type: 'MARK_SAVED' }
  | { type: 'UPDATE_TEXT'; id: string; text: string }
  | { type: 'START_TEXT_EDIT'; id: string }
  | { type: 'STOP_TEXT_EDIT' }
  | { type: 'LIVE_RESIZE_TEXT'; id: string; height: number };

const MAX_UNDO = 50;

function deepCopyShapes(shapes: AnnotationShape[]): AnnotationShape[] {
  return shapes.map((s) => {
    if (s.type === 'freeform') {
      return { ...s, points: s.points.map((p) => ({ ...p })) };
    }
    return { ...s };
  });
}

function pushUndo(state: AnnotationEditorState): AnnotationEditorState {
  const newStack = [...state.undoStack, deepCopyShapes(state.shapes)];
  return {
    ...state,
    undoStack: newStack.length > MAX_UNDO ? newStack.slice(-MAX_UNDO) : newStack,
    redoStack: [],
  };
}

export function createInitialAnnotationState(
  initialShapes: AnnotationShape[] = [],
  defaultColor = '#ff0000',
  defaultTool: AnnotationTool = 'box',
): AnnotationEditorState {
  return {
    shapes: deepCopyShapes(initialShapes),
    selectedIds: new Set<string>(),
    activeTool: defaultTool,
    activeColor: defaultColor,
    drawingShape: null,
    undoStack: [],
    redoStack: [],
    isDirty: false,
    editingTextId: null,
  };
}

export function annotationReducer(
  state: AnnotationEditorState,
  action: AnnotationAction,
): AnnotationEditorState {
  switch (action.type) {
    case 'SET_TOOL':
      return { ...state, activeTool: action.tool, editingTextId: null };

    case 'SET_COLOR':
      return { ...state, activeColor: action.color };

    case 'START_DRAWING':
      return { ...state, drawingShape: action.shape };

    case 'UPDATE_DRAWING':
      return { ...state, drawingShape: action.shape };

    case 'COMMIT_DRAWING': {
      if (!state.drawingShape) return state;
      const withUndo = pushUndo(state);
      return {
        ...withUndo,
        shapes: [...withUndo.shapes, state.drawingShape],
        drawingShape: null,
        isDirty: true,
      };
    }

    case 'CANCEL_DRAWING':
      return { ...state, drawingShape: null };

    case 'INSERT_SHAPE': {
      const withUndo = pushUndo(state);
      return {
        ...withUndo,
        shapes: [...withUndo.shapes, action.shape],
        selectedIds: new Set([action.shape.id]),
        drawingShape: null,
        isDirty: true,
      };
    }

    case 'CYCLE_SELECTION': {
      if (state.shapes.length === 0) return state;
      const ids = state.shapes.map((s) => s.id);
      const currentId = state.selectedIds.size === 1 ? Array.from(state.selectedIds)[0] : null;
      const currentIdx = currentId ? ids.indexOf(currentId) : -1;
      let nextIdx: number;
      if (action.direction === 'next') {
        nextIdx = currentIdx === -1 || currentIdx >= ids.length - 1 ? 0 : currentIdx + 1;
      } else {
        nextIdx = currentIdx <= 0 ? ids.length - 1 : currentIdx - 1;
      }
      return { ...state, selectedIds: new Set([ids[nextIdx]]) };
    }

    case 'SELECT_SHAPE': {
      const newSelected = new Set(action.additive ? state.selectedIds : []);
      if (newSelected.has(action.id)) {
        newSelected.delete(action.id);
      } else {
        newSelected.add(action.id);
      }
      return { ...state, selectedIds: newSelected };
    }

    case 'DESELECT_ALL':
      if (state.selectedIds.size === 0) return state;
      return { ...state, selectedIds: new Set() };

    case 'DELETE_SELECTED': {
      if (state.selectedIds.size === 0) return state;
      const withUndo = pushUndo(state);
      return {
        ...withUndo,
        shapes: withUndo.shapes.filter((s) => !state.selectedIds.has(s.id)),
        selectedIds: new Set(),
        editingTextId: state.editingTextId && state.selectedIds.has(state.editingTextId) ? null : state.editingTextId,
        isDirty: true,
      };
    }

    case 'MOVE_SHAPES': {
      const idSet = new Set(action.ids);
      if (idSet.size === 0) return state;
      const withUndo = pushUndo(state);
      return {
        ...withUndo,
        shapes: withUndo.shapes.map((s) => {
          if (!idSet.has(s.id)) return s;
          switch (s.type) {
            case 'box':
            case 'text':
              return { ...s, x: s.x + action.dx, y: s.y + action.dy };
            case 'circle':
              return { ...s, cx: s.cx + action.dx, cy: s.cy + action.dy };
            case 'line':
            case 'arrow':
              return {
                ...s,
                x1: s.x1 + action.dx,
                y1: s.y1 + action.dy,
                x2: s.x2 + action.dx,
                y2: s.y2 + action.dy,
              };
            case 'freeform':
              return {
                ...s,
                points: s.points.map((p) => ({ x: p.x + action.dx, y: p.y + action.dy })),
              };
            default:
              return s;
          }
        }),
        isDirty: true,
      };
    }

    case 'RESIZE_SHAPE': {
      const withUndo = pushUndo(state);
      return {
        ...withUndo,
        shapes: withUndo.shapes.map((s) => (s.id === action.id ? action.shape : s)),
        isDirty: true,
      };
    }

    case 'ROTATE_SHAPE': {
      const withUndo = pushUndo(state);
      return {
        ...withUndo,
        shapes: withUndo.shapes.map((s) =>
          s.id === action.id ? { ...s, rotation: action.rotation } : s,
        ),
        isDirty: true,
      };
    }

    case 'ROTATE_SELECTED': {
      if (state.selectedIds.size === 0) return state;
      const withUndo = pushUndo(state);
      return {
        ...withUndo,
        shapes: withUndo.shapes.map((s) =>
          state.selectedIds.has(s.id)
            ? { ...s, rotation: (((s.rotation ?? 0) + action.delta) % 360 + 360) % 360 }
            : s,
        ),
        isDirty: true,
      };
    }

    case 'RESIZE_SELECTED': {
      if (state.selectedIds.size === 0) return state;
      const withUndo = pushUndo(state);
      const MIN = 0.005;
      return {
        ...withUndo,
        shapes: withUndo.shapes.map((s) => {
          if (!state.selectedIds.has(s.id)) return s;
          switch (s.type) {
            case 'box':
            case 'text':
              return {
                ...s,
                width: Math.max(MIN, s.width + action.dw),
                height: Math.max(MIN, s.height + action.dh),
              };
            case 'circle':
              return {
                ...s,
                rx: Math.max(MIN / 2, s.rx + action.dw / 2),
                ry: Math.max(MIN / 2, s.ry + action.dh / 2),
              };
            case 'line':
            case 'arrow': {
              // Scale length around midpoint
              const mx = (s.x1 + s.x2) / 2;
              const my = (s.y1 + s.y2) / 2;
              const factor = 1 + action.dw + action.dh;
              return {
                ...s,
                x1: mx + (s.x1 - mx) * factor,
                y1: my + (s.y1 - my) * factor,
                x2: mx + (s.x2 - mx) * factor,
                y2: my + (s.y2 - my) * factor,
              };
            }
            case 'freeform': {
              // Scale all points around centroid
              const cx = s.points.reduce((a, p) => a + p.x, 0) / s.points.length;
              const cy = s.points.reduce((a, p) => a + p.y, 0) / s.points.length;
              const factor = 1 + (action.dw + action.dh) / 2;
              return {
                ...s,
                points: s.points.map((p) => ({
                  x: cx + (p.x - cx) * factor,
                  y: cy + (p.y - cy) * factor,
                })),
              };
            }
            default:
              return s;
          }
        }),
        isDirty: true,
      };
    }

    case 'CHANGE_SELECTED_COLOR': {
      if (state.selectedIds.size === 0) return state;
      const withUndo = pushUndo(state);
      return {
        ...withUndo,
        shapes: withUndo.shapes.map((s) =>
          state.selectedIds.has(s.id) ? { ...s, color: action.color } : s,
        ),
        activeColor: action.color,
        isDirty: true,
      };
    }

    case 'ERASE_AT': {
      const withUndo = pushUndo(state);
      return {
        ...withUndo,
        shapes: action.shapes,
        isDirty: true,
      };
    }

    case 'LOAD_SHAPES':
      return {
        ...state,
        shapes: deepCopyShapes(action.shapes),
        selectedIds: new Set(),
        drawingShape: null,
        undoStack: [],
        redoStack: [],
        isDirty: false,
        editingTextId: null,
      };

    case 'UNDO': {
      if (state.undoStack.length === 0) return state;
      const newUndoStack = [...state.undoStack];
      const previousShapes = newUndoStack.pop()!;
      return {
        ...state,
        shapes: previousShapes,
        undoStack: newUndoStack,
        redoStack: [...state.redoStack, deepCopyShapes(state.shapes)],
        selectedIds: new Set(),
        isDirty: newUndoStack.length > 0,
        editingTextId: null,
      };
    }

    case 'REDO': {
      if (state.redoStack.length === 0) return state;
      const newRedoStack = [...state.redoStack];
      const nextShapes = newRedoStack.pop()!;
      return {
        ...state,
        shapes: nextShapes,
        undoStack: [...state.undoStack, deepCopyShapes(state.shapes)],
        redoStack: newRedoStack,
        selectedIds: new Set(),
        isDirty: true,
        editingTextId: null,
      };
    }

    case 'DISCARD':
      return {
        ...state,
        shapes: state.undoStack.length > 0 ? state.undoStack[0] : [],
        selectedIds: new Set(),
        drawingShape: null,
        undoStack: [],
        redoStack: [],
        isDirty: false,
        editingTextId: null,
      };

    case 'MARK_SAVED':
      return { ...state, isDirty: false };

    case 'UPDATE_TEXT': {
      const withUndo = pushUndo(state);
      return {
        ...withUndo,
        shapes: withUndo.shapes.map((s) =>
          s.id === action.id && s.type === 'text' ? { ...s, text: action.text } : s,
        ),
        isDirty: true,
      };
    }

    case 'START_TEXT_EDIT':
      return { ...state, editingTextId: action.id };

    case 'STOP_TEXT_EDIT':
      return { ...state, editingTextId: null };

    case 'LIVE_RESIZE_TEXT':
      // Live height update during text editing — no undo push, no isDirty change.
      // The final size is captured by commitTextEdit via RESIZE_SHAPE.
      return {
        ...state,
        shapes: state.shapes.map((s) =>
          s.id === action.id ? { ...s, height: action.height } : s,
        ),
      };

    default:
      return state;
  }
}
